/**
* TextWrap.js
* 
* Created on 16-okt-2014, 9:16:42
*
* @author Harm Boschloo for Ranj
* @author Henrique Campos
**/

import { containsCJKCharacters } from "../../util/StringUtil";

export default class TextWrap {
    /**
     * Applies forceful word wrapping to a text by splitting lines that do not fit the given width.
     * 
     * Note: this wrapping approach is destructive as it adds the provided wrap separator string to the text in
     * order to force word wrapping.
     * 
     * @param {string} text the string to wrap
     * @param {number} maxWidth maximum width of each line in pixels
     * @param {CanvasRenderingContext2D} context the Canvas rendering context to determine the text metrics
     * @param {string} wrapSeparator the separator to use when a line is wrapped
     * @returns {string} text after wrapping
     */
    static wrapText(text, maxWidth, context, wrapSeparator = ' ', splitRegex = /(\s|\u200B)/) {
        if (!text) {
            return text;
        }
        // first, split text by newlines
        const originalLines = text.split(/(?:\r\n|\r|\n)/);
        const newLines = [];

        for (let line of originalLines) {
            const lineWidth = context.measureText(line).width;
            if (lineWidth <= maxWidth) {
                newLines.push(line);
            } else {
                const wrappedLine = TextWrap.wrapLine(line, maxWidth, context, wrapSeparator, splitRegex);
                newLines.push(wrappedLine);
            }
        }
        return newLines.join('\n');
    }

    /**
     * Applies forceful word wrapping to a createjs.Text by splitting lines that do not fit the given width.
     * 
     * Note: this approach is useful as createjs.Text only word wraps based on white-spaces.
     * 
     * Note: this wrapping approach is destructive as it adds the provided wrap separator string to the text in
     * order to force word wrapping.
     * 
     * @param {createjs.Text} textField the createjs.Text to wrap
     * @param {number} lineWidth the target line width in pixels; if none is provided it uses the createjs.Text.lineWidth
     */
    static wrapTextField(textField, lineWidth = undefined, wrapSeparator = ' ', splitRegex = /(\s|\u200B)/) {
        // NB: if no line width is provided, try the Text lineWidth
        lineWidth = lineWidth || textField.lineWidth;
        if (!lineWidth) {
            console.error("lineWidth or textField.lineWidth not provided");
            return;
        }

        // NB: apply the CanvasRenderingContext2D to the TextField to determine size of text
        /** @type {CanvasRenderingContext2D} */
        const context = textField._prepContext(createjs.Text._workingContext);
        textField.text = TextWrap.wrapText(textField.text, lineWidth, context, wrapSeparator, splitRegex);
    }

    /**
     * Wraps a single line of text to fit within maxWidth
     * 
     * @param {string} line 
     * @param {number} maxWidth 
     * @param {CanvasRenderingContext2D} context 
     * @param {string} wrapSeparator
     * @return {string}
     */
    static wrapLine(line, maxWidth, context, wrapSeparator = ' ', splitRegex = /(\s|\u200B)/) {
        // split words on the provided separator regex (defaults to any whitespace and ZWSP); returns separators as well
        const parts = line.split(splitRegex);

        const splitLines = [];
        let newLine = "";
        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            const newLineWidth = context.measureText(newLine + part).width;

            if (newLineWidth <= maxWidth) {
                // if text fits, just add it to the new line
                newLine += part;
            } else if (containsCJKCharacters(part)) {
                // split part with latin words and numbers as these should wrapped separately. Ensure there is at least one letter or digit (prevents wrapping of latin punctuation-only parts)
                // NB: this covers the use case of latin words between CJK text. Even in this case, no white spaces are normally used.
                // NB: this only covers latin alphabet, and it does not cover uncommon cases of CJK mixed with other alphabets (e.g., cyrillic)
                // NB: the leading bracket group captures optional open-brackets (e.g. （ [ () ) so they stay
                // attached to the latin word rather than being stranded at the end of the previous CJK token.
                // The trailing bracket group does the same for close-brackets (e.g. ） ] ) ).
                const splitCJKLatin = part.split(/([(（\[\u3010\u300a\u300c\u300e\u3008\u2018\u201c]*[a-zA-Z0-9,.]*[a-zA-Z0-9]+[a-zA-Z0-9,.]*[\u0029\uff09\]\u3011\u300b\u300d\u300f\u3009\u2019\u201d]*)/).filter(s => s !== '');
                // if part splits between CJK and latin/numbers, then add these to the parts and process them again
                if (splitCJKLatin.length > 1) {
                    parts.splice(i, 1, ...splitCJKLatin);
                    i--;
                } else {
                    // if part has CJK characters, ensure that the string is split following proper wrapping rules
                    const splitWord = TextWrap.splitString(newLine + part, maxWidth, context, true);
                    newLine = splitWord[0];
                    // if any - add remainder to the parts so it is processed next
                    if (splitWord.length > 1) {
                        parts.splice(i + 1, 0, splitWord[1]);
                    }
                    splitLines.push(newLine);
                    newLine = "";
                }
            } else if (newLine === "") {
                // if part is too big to even fit by itself in one line, split it
                const wordParts = TextWrap.splitString(part, maxWidth, context);
                // add split word into the parts by replacing its original so the remainder is processed next
                parts.splice(i, 1, ...wordParts);
                // add first part of the word to the lines
                splitLines.push(wordParts[0]);
            } else {
                // if current part does not fit in the new line, then add new line to lines array
                splitLines.push(newLine);
                newLine = "";
                // if current part that wraps the line is not any white space or ZWSP, process it again in the next iteration
                // NB: this ensures that a white space or ZWSP that wraps a line is not rendered again, i.e., causing the
                // next line to start with that white space.
                if (!/\s|\u200B/g.test(part)) {
                    i--;
                }
            }
        }
        splitLines.push(newLine);
        return splitLines.join(wrapSeparator);
    }

    /**
     * Splits a string in two: a width fitting word and the remainder
     * 
     * @param {string} str 
     * @param {number} maxWidth 
     * @param {CanvasRenderingContext2D} context 
     * @param {boolean} isCJK - If true, applies Kinsoku Shori rules for CJK line breaking. If undefined, auto-detects.
     * @returns {Array<string>} where the first fits the width and the second is the remainder
     */
    static splitString(str, maxWidth, context, isCJK = undefined) {

        // NB: do not attempt to split a string that is already 1 character or less
        if (str.length <= 1) {
            return [str];
        }

        // Auto-detect CJK if not explicitly specified
        if (isCJK === undefined) {
            isCJK = containsCJKCharacters(str);
        }

        let firstPart = str;
        let wrapPosition = str.length;

        // iterates through string by removing the last character each time until it fits the textfield's width
        while (wrapPosition > 1) {
            wrapPosition--;
            firstPart = str.substring(0, wrapPosition);

            const isWithinMaxWidth = context.measureText(firstPart).width <= maxWidth;
            if (isWithinMaxWidth) {
                break;
            }
        }

        // check if should apply Kinsoku Shori wrap - i.e. wrap position must decrease until it respect the wrap rules
        const isWrapped = wrapPosition < str.length;
        if (isWrapped && isCJK) {
            let cjkWrapPosition = wrapPosition;
            // While wrap position does not respect Kinsoku Shori wrap rules, decrease the wrap position.
            // NB: In a nutshell, this passes characters from the first part into the remainder until the rule is respected.
            // Check initial position and loop down until we find a valid position
            while (cjkWrapPosition > 0) {
                const isValid = TextWrap.isValidKinsokuShoriWrap(str, cjkWrapPosition);

                if (isValid) {
                    break;
                }
                cjkWrapPosition--;
            }

            // If no valid position found (cjkWrapPosition = 0), return the string unsplit
            if (cjkWrapPosition === 0) {
                return [str];
            }

            // if there is a new first part, assign it and determine new first part
            const hasNewWrapPosition = cjkWrapPosition != wrapPosition;
            if (hasNewWrapPosition) {
                wrapPosition = cjkWrapPosition;
                firstPart = str.substring(0, wrapPosition);
            }
        }
        const remainder = str.substring(wrapPosition);
        // create array where first is the word that fits the width followed by the remainder word
        return [firstPart, remainder];
    }

    /**
     * Determines whether a line may be broken at the given position, following the Kinsoku Shori
     * rules: certain characters may not end a line and certain characters may not start one.
     *
     * NB: exposed for callers that do their own line breaking (e.g. RichTextWrapper, which has to
     * wrap text that is split up over multiple styled parts) and therefore cannot use splitString.
     *
     * @param {string} str the text that is being broken
     * @param {number} wrapPosition index in str of the first character of the next line
     * @returns {boolean} True if a line break at this position is allowed; False, otherwise.
     */
    static isValidKinsokuShoriWrap(str, wrapPosition) {
        // Check if wrap position respects the Kinsoku Shori wrap rules by checking that:
        // - the first part does not end with a invalid character,
        // - or that the remainder part does not start with an invalid character.
        //
        // NB: Kinsoku Shori allows for words to be broken in any part as long as certain characters do not end or
        // start a line.
        const endCharacterFirstPart = str[wrapPosition - 1];
        const startCharacterRemainder = str[wrapPosition];
        return !RegexInvalidLineEndSymbol.test(endCharacterFirstPart) && !RegexInvalidLineStartSymbol.test(startCharacterRemainder);
    }
}

// Line breaking rules in CJK (following Kinsoku Shori as it fits all 4 languages - incl. chinese traditional and simplified)
// Refer to: https://en.wikipedia.org/wiki/Line_breaking_rules_in_East_Asian_languages
// Regex rules based from: https://github.com/huang-yuwei/pixi-cjk/blob/main/src/KinsokuShori.ts
const RegexCannotStartZhCn = /[!%),.:;?\]}¢°·'""†‡›℃∶、。〃〆〕〗〞﹚﹜！＂％＇），．：；？！］｝～]/;
const RegexCannotEndZhCn = /[$(£¥·'"〈《「『【〔〖〝﹙﹛＄（．［｛￡￥]/;
const RegexCannotStartZhTw = /[!),.:;?\]}¢·–—'"•"、。〆〞〕〉》」︰︱︲︳﹐﹑﹒﹓﹔﹕﹖﹘﹚﹜！），．：；？︶︸︺︼︾﹀﹂﹗］｜｝､]/;
const RegexCannotEndZhTw = /[([{£¥'"‵〈《「『〔〝︴﹙﹛（｛︵︷︹︻︽︿﹁﹃﹏]/;
const RegexCannotStartJaJp = /[)\]｝〕〉》」』】〙〗〟'"｠»ヽヾーァィゥェォッャュョヮヵヶぁぃぅぇぉっゃゅょゎゕゖㇰㇱㇲㇳㇴㇵㇶㇷㇸㇹㇺㇻㇼㇽㇾㇿ々〻‐゠–〜?!‼⁇⁈⁉・、:;,。.]/;
const RegexCannotEndJaJp = /[([｛〔〈《「『【〘〖〝'"｟«—...‥〳〴〵]/;
const RegexCannotStartKoKr = /[!%),.:;?\]}¢°'"†‡℃〆〈《「『〕！％），．：；？］｝]/;
const RegexCannotEndKoKr = /[$([{£¥'"々〇〉》」〔＄（［｛｠￥￦#]/;

const RegexInvalidLineStartSymbol = new RegExp(
    `${RegexCannotStartZhCn.source}|${RegexCannotStartZhTw.source}|${RegexCannotStartJaJp.source}|${RegexCannotStartKoKr.source}`
);

const RegexInvalidLineEndSymbol = new RegExp(
    `${RegexCannotEndZhCn.source}|${RegexCannotEndZhTw.source}|${RegexCannotEndJaJp.source}|${RegexCannotEndKoKr.source}`
);